import { useMemo } from "react";
import type { BaseEntity } from "../../../UniversalCRMTable/types";
import type {
  AuthState,
  Callbacks,
  Handlers,
  InfiniteScrollState,
  PageConfig,
  ViewModeState,
} from "../../contexts/types";
import type {
  CompositionContextValueParams,
  CompositionNavigation,
} from "./compositionContextValueTypes";

/**
 * The STABLE half of the page context.
 *
 * None of these change while the user types in the search box, so a consumer
 * that reads only from here (board views, the grouping wrapper, the header's
 * action buttons) is untouched by a keystroke. Each slice is memoized on its
 * OWN inputs — built as one 85-dependency `useMemo`, every one of them was
 * re-allocated whenever any single dependency anywhere moved.
 */
export interface CompositionStableSlices<T extends BaseEntity> {
  pageConfig: PageConfig<T>;
  auth: AuthState;
  navigation: CompositionNavigation;
  handlers: Handlers<T>;
  callbacks: Callbacks<T>;
  mutations: { deleteMutation: { isPending: boolean } };
  viewModeState: ViewModeState;
  infiniteScrollState: InfiniteScrollState;
}

export function useCompositionStableSlices<T extends BaseEntity>(
  params: CompositionContextValueParams<T>
): CompositionStableSlices<T> {
  const pageConfig = useMemo<PageConfig<T>>(
    () => ({
      entityName: params.entityName,
      config: params.config,
      hooks: params.hooks,
      entityFormProps: params.entityFormProps,
      customComponents: params.customComponents,
    }),
    [
      params.entityName,
      params.config,
      params.hooks,
      params.entityFormProps,
      params.customComponents,
    ]
  );

  const auth = useMemo<AuthState>(
    () => ({
      appUser: params.appUser,
      isAuthenticated: params.isAuthenticated,
      isClientUser: params.isClientUser,
      authLoading: params.authLoading,
      isEmployee: params.isEmployee,
      isAdmin: params.isAdmin,
      isWTCCRM: params.isWTCCRM,
      isWTCEmployee: params.isWTCEmployee,
      isAdminRole: params.isAdminRole,
      isMobile: params.isMobile,
      canCreate: params.canCreate,
    }),
    [
      params.appUser,
      params.isAuthenticated,
      params.isClientUser,
      params.authLoading,
      params.isEmployee,
      params.isAdmin,
      params.isWTCCRM,
      params.isWTCEmployee,
      params.isAdminRole,
      params.isMobile,
      params.canCreate,
    ]
  );

  // Rebuilt from the four individual fields rather than passed through, so an
  // upstream object rebuilt with identical contents does not invalidate it.
  const navigation = useMemo<CompositionNavigation>(
    () => ({
      router: params.navigation.router,
      searchParams: params.navigation.searchParams,
      apolloClient: params.navigation.apolloClient,
      activeViewId: params.navigation.activeViewId,
    }),
    [
      params.navigation.router,
      params.navigation.searchParams,
      params.navigation.apolloClient,
      params.navigation.activeViewId,
    ]
  );

  const handlers = useMemo<Handlers<T>>(
    () => ({
      handleCreateItem: params.handlers.handleCreateItem,
      handleEditItem: params.handlers.handleEditItem,
      handleInlineEdit: params.handlers.handleInlineEdit,
      handleDeleteItem: params.handlers.handleDeleteItem,
      handleViewItem: params.handlers.handleViewItem,
      handleItemCreated: params.handlers.handleItemCreated,
      handleItemUpdated: params.handlers.handleItemUpdated,
      handleItemDeleted: params.handlers.handleItemDeleted,
      handleImportItems: params.handlers.handleImportItems,
      handleCancelDelete: params.handlers.handleCancelDelete,
      handleExportWithScope: params.handlers.handleExportWithScope,
      handleExportItems: params.handlers.handleExportItems,
      handleSavedViewsReady: params.handlers.handleSavedViewsReady,
      handleComposeMessage: params.handlers.handleComposeMessage,
      handleOpenDetailModal: params.handlers.handleOpenDetailModal,
    }),
    [
      params.handlers.handleCreateItem,
      params.handlers.handleEditItem,
      params.handlers.handleInlineEdit,
      params.handlers.handleDeleteItem,
      params.handlers.handleViewItem,
      params.handlers.handleItemCreated,
      params.handlers.handleItemUpdated,
      params.handlers.handleItemDeleted,
      params.handlers.handleImportItems,
      params.handlers.handleCancelDelete,
      params.handlers.handleExportWithScope,
      params.handlers.handleExportItems,
      params.handlers.handleSavedViewsReady,
      params.handlers.handleComposeMessage,
      params.handlers.handleOpenDetailModal,
    ]
  );

  const callbacks = useMemo<Callbacks<T>>(
    () => ({
      handleGroupingExport: params.callbacks.handleGroupingExport,
      renderSimpleTable: params.callbacks.renderSimpleTable,
    }),
    [params.callbacks.handleGroupingExport, params.callbacks.renderSimpleTable]
  );

  const mutations = useMemo(
    () => ({ deleteMutation: params.mutations.deleteMutation }),
    [params.mutations.deleteMutation]
  );

  const viewModeState = useMemo<ViewModeState>(
    () => ({ viewMode: params.viewMode, setViewMode: params.setViewMode }),
    [params.viewMode, params.setViewMode]
  );

  const infiniteScrollEnabled = params.paginationModeState.paginationMode.mode === "infinite-scroll";
  const toggleInfiniteScroll = params.paginationModeState.toggleInfiniteScroll;
  const infiniteScrollState = useMemo<InfiniteScrollState>(
    () => ({
      infiniteScrollEnabled,
      setInfiniteScrollEnabled: toggleInfiniteScroll,
    }),
    [infiniteScrollEnabled, toggleInfiniteScroll]
  );

  return {
    pageConfig,
    auth,
    navigation,
    handlers,
    callbacks,
    mutations,
    viewModeState,
    infiniteScrollState,
  };
}
